import { Archive, CircleCheck, CircleDashed, Contrast, Reply, type LucideIcon } from 'lucide-react';
import type { SupportTicketSummaryDto } from '@/api/control/missionImpactAPI.schemas';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useLanguage } from '@/contexts/LanguageContext';
import { SUPPORT_STATE_CLASSES, getSupportTicketState, type SupportTicketState } from '@/lib/support';
import { cn } from '@/lib/utils';

interface SupportStatusBadgeProps {
  ticket: Pick<SupportTicketSummaryDto, 'status' | 'needsCustomerReply'>;
  className?: string;
}

/** The icon shown with each state, so the pill never relies on colour alone. */
const STATE_ICONS: Record<SupportTicketState, LucideIcon> = {
  WaitingOnYou: Reply,
  Open: CircleDashed,
  InProgress: Contrast,
  Resolved: CircleCheck,
  Closed: Archive,
};

/**
 * Status pill for a support ticket: an icon and a word, with a tooltip explaining what it means.
 * Shows "Waiting on you" when support is waiting on the customer's reply.
 */
export function SupportStatusBadge({ ticket, className }: SupportStatusBadgeProps) {
  const { t } = useLanguage();
  const state = getSupportTicketState(ticket);
  if (!state) return null;
  const Icon = STATE_ICONS[state];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
            SUPPORT_STATE_CLASSES[state],
            className
          )}
        >
          <Icon className="h-3 w-3" aria-hidden />
          {t.support.status[state]}
        </span>
      </TooltipTrigger>
      <TooltipContent>{t.support.status[`${state}Tooltip`]}</TooltipContent>
    </Tooltip>
  );
}
